import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight, Atom, Radiation, FlaskConical, Microscope,
  Syringe, Wind, Eye, Droplets, Leaf, Brain, ArrowDown,
} from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";

const services = [
  { icon: FlaskConical, title: "Formulation Development", items: ["Preformulation", "Formulation Design", "QbD & Nanotechnology", "Analytical Services"] },
  { icon: Radiation, title: "Pharmaco-Scintigraphy", items: ["Human & Preclinical Scintigraphy", "Radiolabeling Validation", "Radiolabel Development", "Pharmacodynamic Studies"] },
  { icon: Microscope, title: "Clinical Studies", items: ["Proof-of-Concept", "Investigator Initiated Trials", "Phase 0, I & II Trials", "Pharmacokinetic Studies"] },
  { icon: Atom, title: "Product Evaluation", items: ["Dissolution", "Dermatokinetics", "Skin Permeation", "Preclinical Safety & Efficacy"] },
];

const products = [
  { icon: Syringe, title: "Parenteral", desc: "IV, IM, IP & SC — biodistribution, pharmacokinetics and relative bioequivalence." },
  { icon: Wind, title: "Pulmonary", desc: "Lung deposition patterns and mucociliary clearance for DPI & MDI devices." },
  { icon: Eye, title: "Ocular & Nasal", desc: "Device, formulation and patient-factor optimisation for precise delivery." },
  { icon: Droplets, title: "Topical", desc: "Dermatokinetics, skin permeation and evaluation of transdermal patches." },
  { icon: Leaf, title: "Herbal & Nutraceutical", desc: "In vivo behaviour, morphology and targeting potential of natural actives." },
  { icon: Brain, title: "Nose-to-Brain", desc: "Direct CNS delivery pathways with quantified targeting efficiency." },
];

const chapters = [
  { n: "01", t: "A century of expertise", d: "Twenty years of individual and one hundred years of cumulative experience in radiolabeling of APIs and conducting scintigraphy clinical trials — a genuinely unique capability in India." },
  { n: "02", t: "Global-first inventions", d: "Inventors of surface labeling and pioneers of DPI & MDI labeling at a global level, giving every formulation we touch an unmatched analytical depth." },
  { n: "03", t: "See inside the body", d: "Gamma scintigraphy — a non-invasive, USFDA-recognised tool that reveals disintegration time, gastric emptying, GI transit and site-specific deposition, live and in vivo." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);

  return (
    <div className="relative overflow-hidden bg-background">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] grain">
        <div className="pointer-events-none absolute -right-40 top-10 h-[520px] w-[520px] rounded-full bg-gold/10 blur-[120px]" />
        <div className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="mx-auto grid max-w-[1400px] items-center gap-8 px-6 pb-16 pt-36 lg:grid-cols-[1.12fr_0.88fr] lg:px-10 lg:pt-44">
          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mb-7 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
              Drug Formulation & Delivery Science
            </motion.div>

            <h1 className="font-serif-display text-5xl font-semibold leading-[0.98] tracking-tight text-foreground md:text-6xl lg:text-[5.4rem]">
  <MaskedLines lines={["Science that", "redefines"]} />
  <span className="block overflow-hidden pb-5 -mb-5">
    <motion.span
      className="block text-primary"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
    >
      your brand.
    </motion.span>
  </span>
</h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-8 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg"
            >
              We solve the hardest problems in drug development — engineering formulations,
              proving them through in&nbsp;vitro and in&nbsp;vivo testing, and visualising their
              journey with gamma scintigraphy and proof-of-concept studies.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-semibold text-primary-foreground shadow-luxe transition-colors duration-300 hover:bg-gold"
              >
                Start a Project
                <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="rounded-full border border-border bg-card px-7 py-3.5 font-medium text-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
              >
                Explore Services
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="mt-14 flex gap-10"
            >
              {[["100+", "Years cumulative"], ["USFDA", "Recognised methods"], ["#1", "Scintigraphy in India"]].map(([a, b]) => (
                <div key={b}>
                  <div className="font-serif-display text-3xl font-semibold text-primary">{a}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">{b}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Scintigraphy spotlight image with 3D parallax */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto hidden w-full max-w-sm card-3d lg:mx-0 lg:block"
          >
            <motion.div
              style={{ rotateZ: imgRotate }}
              className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-border bg-card shadow-luxe-lg"
            >
              <motion.img
                src="/assets/hero.jpg"
                alt="Gamma scintigraphy tracer hotspots"
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover object-center"
              />
              <div className="absolute bottom-5 left-5 rounded-full border border-border bg-background/85 px-4 py-2 text-xs text-foreground backdrop-blur-md">
                <span className="text-primary">●</span> Live tracer distribution · Gamma camera
              </div>
            </motion.div>
            <div className="absolute -left-6 top-10 rounded-2xl border border-gold/40 bg-card/90 px-5 py-4 shadow-luxe backdrop-blur-md">
              <div className="font-serif-display text-2xl font-semibold text-gold">20 yrs</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Radiolabeling</div>
            </div>
          </motion.div>
        </div>

        <div className="pointer-events-none absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-muted-foreground">
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-border bg-card py-6">
        <Marquee speed={40} gradient={false} className="overflow-hidden">
          {["Parenteral", "Pulmonary", "Ocular", "Intra-Nasal", "Oral", "Topical", "Nose-to-Brain", "Radiolabeling", "Scintigraphy", "Nanotechnology"].map((w, i) => (
            <span key={i} className="mx-8 font-serif-display text-4xl font-semibold uppercase text-stroke md:text-6xl">
              {w} <span className="text-gold">✳</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-40">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Why choose us</p>
          <h2 className="mt-4 max-w-3xl font-serif-display text-4xl font-semibold leading-[1.02] tracking-tight text-foreground md:text-5xl lg:text-6xl">
            A unique standing in Indian pharmaceutical science.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-3">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12} className="group bg-card p-8 transition-colors duration-500 hover:bg-muted/40 lg:p-10">
              <span className="font-serif-display text-6xl font-semibold text-primary/90 transition-colors duration-300 group-hover:text-gold">{c.n}</span>
              <h3 className="mt-6 font-heading text-2xl font-semibold text-foreground">{c.t}</h3>
              <p className="mt-4 leading-relaxed text-muted-foreground">{c.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative border-t border-border bg-muted/40">
        <div className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-36">
          <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-gold">What we do</p>
              <h2 className="mt-4 font-serif-display text-4xl font-semibold tracking-tight text-foreground md:text-5xl lg:text-6xl">Full-spectrum solutions</h2>
            </div>
            <Link to="/services" className="group inline-flex items-center gap-2 text-muted-foreground transition-colors duration-300 hover:text-primary">
              All services <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>

          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className="group relative bg-card p-8 transition-colors duration-500 hover:bg-background lg:p-12">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/5 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
                  <s.icon className="h-7 w-7" strokeWidth={1.5} />
                </div>
                <h3 className="mt-6 font-heading text-2xl font-semibold text-foreground md:text-3xl">{s.title}</h3>
                <ul className="mt-6 space-y-2.5">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-center gap-3 text-muted-foreground">
                      <span className="h-1 w-1 rounded-full bg-gold" /> {it}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-36">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Dosage forms we master</p>
          <h2 className="mt-4 font-serif-display text-4xl font-semibold tracking-tight text-foreground md:text-5xl lg:text-6xl">Every route of delivery.</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.06}>
              <div className="group h-full rounded-2xl border border-border bg-card p-8 shadow-luxe transition-transform duration-300 hover:-translate-y-1.5 hover:border-gold/50">
                <p.icon className="h-8 w-8 text-primary transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
                <h3 className="mt-5 font-heading text-xl font-semibold text-foreground">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLIENTS */}
      <section className="border-y border-border bg-muted/40">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Trusted by industry leaders</p>
            <h2 className="mt-3 font-serif-display text-2xl font-semibold text-foreground md:text-3xl">The names that shape Indian pharma</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-luxe">
              <img src="/assets/clients.png" alt="Clients: Cipla, Abbott, Dr. Reddy's, Glenmark, Cadila and more" className="mx-auto w-full max-w-5xl" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-primary">
        <div className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-gold/20 blur-[100px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-40">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold-soft">Make our customers happy</p>
            <h2 className="mt-5 max-w-4xl font-serif-display text-4xl font-semibold leading-[1.02] tracking-tight text-primary-foreground md:text-6xl lg:text-7xl">
              Let&apos;s solve your toughest <span className="text-gold-soft">formulation</span> challenge.
            </h2>
            <Link
              to="/contact"
              data-testid="cta-bottom"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-background px-8 py-4 text-lg font-semibold text-primary shadow-luxe-lg transition-colors duration-300 hover:bg-gold hover:text-primary-foreground"
            >
              Share your project brief
              <ArrowUpRight className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}